/*
    Universal PDF generation library which can be used for generating inline documents in the browser, or
    run server side to generate documents in batch mode.

    For server side or React versions, use as follows:

    const pdf = require ('pdf/pdf.js');
    pdf.initialise();
    pdf.outputTextCentred("Example Title ", 24);
    pdf.outputTextCentred("School Ready Passport", 10);
    pdf.outputTextCentred("Developed by Seven 360", 10, {fontStyle: 'italic'});
    pdf.stream();

    For node.js, write the document as:

    pdf.save ( '/tmp/filename.pdf' );

 */

const { jsPDF } = require("jspdf"); // will automatically load the node version

const { applyPlugin } = require( 'jspdf-autotable');

const { HTMLtoDOM} = require ( './htmlParser')

applyPlugin(jsPDF)

let doc = null ;
let documentOptions = {} ;

const DEFAULT_TOP_MARGIN = 60 ;
const DEFAULT_BOTTOM_MARGIN = 60 ;
const DEFAULT_LEFT_MARGIN = 60 ;
const DEFAULT_RIGHT_MARGIN = 60 ;

const TOP_MARGIN = 60 ;
const DEFAULT_NUM_COLUMNS = 1 ;
const DEFAULT_COLUMN_PADDING = 20 ;

let bottomMargin = DEFAULT_BOTTOM_MARGIN ;
let leftMargin = DEFAULT_LEFT_MARGIN ;
let rightMargin = DEFAULT_RIGHT_MARGIN ;
let topMargin = DEFAULT_TOP_MARGIN ;

const lineSpacing = 6 ;

const columnMargin = 20 ;

let curY = TOP_MARGIN ;

let currentPage = 1 ;

const normalFontSize = 9 ;
const titleFontSize = 16 ;

const indentSize = 20 ;

const cellPaddingDefault = 5 ;

let baseline = 'alphabetic';

const initialise = ( options, orientation ) =>
{
    let orientationParam = 'p';

    if ( options !== null && options !== undefined )
    {
        documentOptions = options ;
    }

    if ( orientation === null || orientation === undefined )
    {
        orientationParam = 'p' ;
    }
    else
    {
        orientationParam = 'l';
    }

    curY = topMargin ;

    doc = new jsPDF( orientationParam, "pt", "a4", true);
    doc.setFont("Helvetica");

    // Load additional required font files
    if ( options.additionalFonts !== undefined )
    {
        addFonts(options.additionalFonts) ;
    }

    if ( options.baseline !== undefined )
    {
        baseline = options.baseline ;
    }

    if ( options.lineHeightFactor !== undefined )
    {
        const lineHeightFactor = options.lineHeightFactor ;

        doc.setLineHeightFactor( lineHeightFactor ) ;
    }

    doc.page = 1;
}

const ptToPx = ( pt ) =>
{
    return (96 / 72) * pt ;
}

const pxToPt = ( px ) =>
{
    return px * ( 72 / 96 )
}

const addFonts = ( fonts ) =>
{
    for ( let i = 0 ; i < fonts.length ; i++)
    {
        doc.addFileToVFS(fonts[i].filename, fonts[i].base64Font);
        doc.addFont(fonts[i].filename, fonts[i].fontName, fonts[i].style);
    }
}

const setFont = ( name, style ) =>
{
    doc.setFont( name, style ) ;
}

const save = ( filename ) =>
{
    doc.save ( filename ) ;
}

const getDocHeight = () =>
{
    return doc.internal.pageSize.getHeight();
}

const getPrintWidth = () =>
{
    return getDocWidth() - leftMargin - rightMargin ;
}

const getDocWidth = () =>
{
    return doc.internal.pageSize.getWidth();
}

const getTopMargin = () =>
{
    return topMargin ;
}

const getLeftMargin = () =>
{
    return leftMargin ;
}

const getRightMargin = () =>
{
    return rightMargin ;
}

const outputTextCentred = ( text, fSize, style, color ) =>
{
    const textColor = color === undefined ? '#000000': color;

    doc.setFontSize ( fSize ) ;
    doc.setTextColor ( textColor );

    if ( style!= null && style.fontStyle != null )
    {
        const curFont = doc.getFont() ;

        doc.setFont ( curFont.fontName, style.fontStyle ) ;
    }

    let printableText = text === null ? '<null>' : text ;

    let textWidth =  doc.getStringUnitWidth(printableText) ;

    let dim = doc.getTextDimensions(printableText);

    let fontSize = doc.internal.getFontSize() ;

    let stringWidth = textWidth * fontSize ;

    let xOffset = ( doc.internal.pageSize.getWidth()  - stringWidth ) / 2 ;

    doc.text(printableText, xOffset, curY, { baseline: baseline } );

    let ht = doc.internal.getLineHeight() ;

    let h = ht * doc.getLineHeightFactor();

    curY += h;
}

const insertDraftWatermark = () =>
{
    doc.setFontSize(70);

    const tempTextColor = doc.getTextColor() ;

    doc.setTextColor ( '#CCCCCC' );

    const imageYpos = getDocHeight() - 180 ;

    const imageXpos = (getDocWidth() - 70) / 2;

    doc.text("Draft", imageXpos, imageYpos, {angle: 45});

    doc.setTextColor ( tempTextColor );

}

const insertImageCentred = ( imgData ) =>
{
    const imageWidth = getDocWidth () * .3;
    const imageYpos = imageWidth / 2;

    const imageXpos = (getDocWidth() - imageWidth) / 2;

    doc.addImage(imgData, 'PNG', imageXpos, imageYpos, imageWidth, imageWidth);

    curY = imageYpos + imageWidth + 60;
}
const insertBackgroundImage = ( imgData) =>
{
    const imageWidth = getDocWidth ();
    const imageHeight = getDocHeight ();

    doc.addImage(imgData, 'PNG', 0, 0,  imageWidth, imageHeight );
}

const insertInvoiceImage = ( imgData, width, height, shouldMoveCursor ) =>
{
    const onPageImageWidth = ( getDocWidth () - leftMargin - rightMargin ) * .3;
    const imageYpos = curY;

    const imageXpos = getDocWidth() - onPageImageWidth - rightMargin ;

    const pdfToImageRatio = onPageImageWidth / width ;
    const onPageImageHeight = height * pdfToImageRatio ;

    doc.addImage(imgData, 'PNG', imageXpos, imageYpos, onPageImageWidth, onPageImageHeight );

    if ( shouldMoveCursor === true )
    {
        curY = curY + onPageImageHeight ;
    }
}

const insertAllAboutMeImage = ( imgData, width, height, shouldMoveCursor ) =>
{
    const onPageImageWidth = ( getDocWidth () - leftMargin - rightMargin ) * .3;
    const imageYpos = curY - 30 ;

    const imageXpos = getDocWidth() - onPageImageWidth - rightMargin ;

    const pdfToImageRatio = onPageImageWidth / width ;
    const onPageImageHeight = height * pdfToImageRatio ;

    doc.addImage(imgData, 'PNG', imageXpos, imageYpos, onPageImageWidth, onPageImageHeight );

    if ( shouldMoveCursor === true )
    {
        curY = curY + onPageImageHeight ;
    }
}

const addImage = ( img, type, imageXpos, imageYpos, actualWidth, actualHeight, rotation ) =>
{
    doc.addImage(img, type, imageXpos, imageYpos, actualWidth, actualHeight, undefined, undefined, rotation);
}

const insertGraphImageCentred = ( imgData ) =>
{
    const imageWidth = imgData.width ;
    const imageHeight = imgData.height ;

    const ratio = imageWidth / imageHeight ;

    const imageYpos = curY;

    const pageWidth = getDocWidth() ;

    const actualWidth = pageWidth * .8 ;
    const actualHeight = actualWidth / ratio ;

    const imageXpos = (pageWidth - actualWidth) / 2;

    doc.addImage(imgData.image, 'PNG', imageXpos, imageYpos, actualWidth, actualHeight);

    curY = imageYpos + actualHeight + 60;
}

const drawTriangles = () =>
{
    doc.setLineWidth(1);
    doc.setDrawColor(255,0,0);
    doc.setFillColor(0,0,255);
    doc.triangle(0, 20, 110, 100, 120, 500, 'F');
}

const drawBorder = ( pos, radius, style ) =>
{
    const twoPos = pos * 2 ;
    const fillColor = style?.fillColor === undefined ? '#000000' : style.fillColor;
    const width = style?.width === undefined ? 1 : style.width;

    //doc.setDrawColor(0,0,255);
    doc.setDrawColor(fillColor);
    doc.setLineWidth(width);

    doc.roundedRect(pos, pos, getDocWidth(doc) - twoPos, getDocHeight(doc) - twoPos, radius, radius);

}

const drawMargins = () =>
{
    const width = getDocWidth() ;
    const height = getDocHeight() ;

    const lineWidth = width - ( 2 * leftMargin ) ;
    const lineHeight = height - topMargin - bottomMargin ;

    doc.rect ( leftMargin, topMargin, lineWidth, lineHeight) ;
}

const getCurY = () =>
{
    return curY  ;
}

const setCurY = ( value ) =>
{
    curY = value ;
}

const getCurrentPosition = () =>
{
    return { curPage: currentPage, curY: curY } ;
}

const lineFeed = ( lines ) =>
{
    const fontSize = doc.internal.getFontSize() ?? normalFontSize ;

    const factor = doc.getLineHeightFactor() ;

    const gap = getLineSpacingForFontSize( fontSize, factor ) ;

    curY += lines * gap ;
}

const addPageFooter = ( pageNum ) =>
{
    doc.setFontSize(10);

    const tempTextColor = doc.getTextColor() ;

    doc.setTextColor ( '#000000' );

    let height = getDocHeight(doc)

    const text = "Page " + pageNum ;

    let textWidth =  doc.getStringUnitWidth(text) ;

    let fontSize = doc.internal.getFontSize() ;

    let stringWidth = textWidth * fontSize ;

    let xOffset = ( getDocWidth(doc) - stringWidth ) / 2 ;

    doc.text(text, xOffset, height - 30, { baseline: baseline } );

    doc.setTextColor ( tempTextColor );
};

const getLineSpacingForFontSize = ( fontSize, factor ) =>
{
    const multiply = factor === undefined ? doc.getLineHeightFactor() : factor ;

    return fontSize * multiply ;
}

const addPage = ()  =>
{
    doc.addPage() ;

    currentPage += 1 ;

    // Reset the page position pointer
    curY = TOP_MARGIN ;
}

const setPage = ( pageNumber )  =>
{
    doc.setPage(pageNumber) ;
}

const setFontStyle = ( style ) =>
{
    const curFont = doc.getFont() ;

    doc.setFont ( curFont.fontName, style) ;
    doc.setFontSize(normalFontSize);
}

/*
    getColumnCoOrdinates

    Returns an array detailing the left X co-ordinates and widths for each column given a fixed number of columns
    and a padding value in pixels

    The returned array is as:

    [
        { left: 10, width: 100 },
        { left: 200, width: 100 }
    ]
*/
const getAbsoluteColumnCoOrdinates = ( { numberOfColumns, rightMargin, leftMargin, columnPadding }) =>
{
    const defs = [];

    const trueLeftMargin = leftMargin === undefined ? DEFAULT_LEFT_MARGIN : +(leftMargin) ;
    const trueRightMargin = rightMargin === undefined ? DEFAULT_RIGHT_MARGIN : +(rightMargin) ;

    const pageWidth = getDocWidth();

    const drawAreaWidth = pageWidth - (trueLeftMargin + trueRightMargin);

    // Total drawable area of all columns
    const totalColWidth = drawAreaWidth - (columnPadding * (numberOfColumns - 1));

    // Equal column width required ( excluding padding )
    const colWidth = totalColWidth / numberOfColumns;

    for (let curCol = 0; curCol < numberOfColumns; curCol++)
    {
        const colLeft = trueLeftMargin + (colWidth * curCol) + (columnPadding * curCol);

        defs.push({left: colLeft, width: colWidth});
    }

    return defs ;
}

/*
    outputColumns

    Draws columns of text according to the definition below.

    const columnContent = {
        numberOfColumns: 2,
        columnPadding: 20,
        borderStyle: 'none',
        content: [
            [
                {text: 'What I find difficult', style: headerStyle3},
                {
                    text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras libero dui, dictum ac tellus sed, congue interdum ante. Sed purus mauris, tempus sed mi ac, consectetur pharetra odio. Integer hendrerit tempus tempor. Nulla magna metus, rutrum vel lacus in, ultricies porttitor nunc. Nam sit amet arcu eu turpis sodales gravida vel at augue. ',
                    style: paraStyleStyle
                },
                {
                    text: 'Duis hendrerit velit quis urna faucibus, laoreet semper orci facilisis. Nullam quam turpis, malesuada posuere erat in, accumsan eleifend nibh. In rhoncus congue vestibulum.',
                    style: paraStyleStyle
                },
            ],
            [
                {text: 'What helps me to learn (useful strategies)', style: headerStyle4},
                {
                    text: 'Donec tristique mi lacus, lacinia suscipit erat sodales nec. Cras urna risus, commodo sit amet ullamcorper sit amet, viverra a nunc. Aenean lacus nibh, consectetur ut justo at, egestas rutrum felis. Cras convallis nisi at eros feugiat, vel efficitur orci laoreet. Etiam in molestie odio, et rhoncus lectus.',
                    style: paraStyleStyle
                }
            ]
        ]
    };

    content is an array of columns definitions.

    Each column comprises an array of paragraphs to be drawn in the column
 */

const test = () =>
{
    drawMargins() ;
}

const drawColumnBorders = ( columnDef ) =>
{
    const numberOfColumns = columnDef?.numberOfColumns === undefined ? DEFAULT_NUM_COLUMNS : +(columnDef.numberOfColumns) ;
    const columnPadding = columnDef?.columnPadding === undefined ? DEFAULT_COLUMN_PADDING : +(columnDef.columnPadding) ;
    const borderStyle = columnDef?.borderStyle === undefined ? 'none' : columnDef.borderStyle ;

    const content = columnDef.content ;

    if ( content.length !== numberOfColumns )
    {
        throw new Error ( "Column count and number of definitions must be equal") ;
    }

    let initialY = columnDef.startY === undefined ? getCurY() : columnDef.startY ;

    const colDefinitions = getAbsoluteColumnCoOrdinates( {numberOfColumns, columnPadding } ) ;

    for ( let curCol = 0 ; curCol < numberOfColumns ; curCol ++  )
    {
        doc.rect ( colDefinitions[curCol].left, initialY, colDefinitions[curCol].width, 300) ;
    }
}

const colMultiplier = (width, nonAutoTotal) =>
{
    if (width === 'auto')
    {
        return (100 - nonAutoTotal)/100;
    }
    else
    {
        return (nonAutoTotal)/100;
    }
}

const getTabbedColumnDefs = ( columnData, padding ) =>
{
    const defs = [];

    const pageWidth = getDocWidth();

    const drawAreaWidth = pageWidth - (leftMargin + rightMargin);

    // Total drawable area of all columns
    const totalColWidth = drawAreaWidth - (padding * (columnData.length - 1));

    for (let i = 0; i < columnData.length; i++)
    {

        let colWidth;

        let nonAutoTotal = 0;

        for (let j = 0; j < columnData.length; j++)
        {
            if (columnData[j].width !== 'auto')
            {
                nonAutoTotal += +columnData[j].width;
            }
        }

        const multiplier = colMultiplier(columnData[i].width, nonAutoTotal);


        colWidth = totalColWidth * multiplier;

        let widthOfBeforeCol;

        if (i !== 0)
        {
            widthOfBeforeCol = totalColWidth * colMultiplier(columnData[(i-1)].width, nonAutoTotal);
        }
        else
        {
            widthOfBeforeCol = 0;
        }

        const colLeft = leftMargin + (padding * i) + (widthOfBeforeCol);

        defs.push({left: colLeft, width: colWidth});

    }

    return defs;
}

const createObjectToBeRendered = (colDefinitions, columnData) =>
{
    let dataToBeRendered = {cols: []};

    for ( let curCol = 0 ; curCol < columnData.length ; curCol ++  )
    {
        const colLeft = colDefinitions[curCol].left ;
        const colWidth = colDefinitions[curCol].width ;

        const splitParagraph = doc.splitTextToSize(columnData[curCol].text, colWidth);

        let thisColumnData = [];

        for (let i = 0; i < splitParagraph.length; i++)
        {
            thisColumnData.push({text: splitParagraph[i], x: colLeft})
        }

        dataToBeRendered.cols.push(thisColumnData);
    }

    return dataToBeRendered;
}

const tabbedColumnControl = ( columnData ) =>
{
    //Define variables

    const initialPageY = getCurY(); //Starting Y value for this row

    const colDefinitions = getTabbedColumnDefs(columnData, DEFAULT_COLUMN_PADDING);

    const pageHeight = getDocHeight();

    const gutterThreshold = pageHeight - DEFAULT_BOTTOM_MARGIN;

    const lineHeight = getLineSpacingForFontSize(normalFontSize);

    const formattedColumnData = createObjectToBeRendered(colDefinitions, columnData);

    //Get the max length of one of the columns

    let colLengths = [];

    for (let i = 0; i < formattedColumnData.cols.length; i++)
    {
        colLengths.push(formattedColumnData.cols[i].length);
    }

    const longestCol = Math.max(...colLengths);

    //Find if the row height is greater than the space left on the bottom of the page

    const rowHeight = longestCol * lineHeight;

    if (initialPageY + rowHeight > gutterThreshold)
    {
        addPage();
        setCurY(TOP_MARGIN); //Sets the Y value to the top of a new page
    }

    //Draw the columns

    for (let i = 0; i < formattedColumnData.cols.length; i++)
    {
        let curPos = getCurY();

        for (let j = 0; j < formattedColumnData.cols[i].length; j++)
        {
            doc.text(formattedColumnData.cols[i][j].text, formattedColumnData.cols[i][j].x, curPos, { baseline: baseline } );

            curPos += lineHeight;
        }
    }

    //Add the height of the row to the initial y + a line height / 2

    setCurY(getCurY() + rowHeight + lineHeight/2);

}

const outputColumns = ( columnDef ) =>
{
    if ( columnDef.debug === true )
    {
        drawColumnBorders ( columnDef ) ;
    }

    // Preserve the current font config
    const curFont = doc.getFont() ;

    const rightMargin = columnDef?.rightMargin === undefined ? getRightMargin() : columnDef.rightMargin ;
    const leftMargin = columnDef?.leftMargin === undefined ? getLeftMargin() : columnDef.leftMargin ;
    const numberOfColumns = columnDef?.numberOfColumns === undefined ? DEFAULT_NUM_COLUMNS : +(columnDef.numberOfColumns) ;
    const columnPadding = columnDef?.columnPadding === undefined ? DEFAULT_COLUMN_PADDING : +(columnDef.columnPadding) ;
    const borderStyle = columnDef?.borderStyle === undefined ? 'none' : columnDef.borderStyle ;

    const content = columnDef.content ;

    if ( content.length !== numberOfColumns )
    {
        throw new Error ( "Column count and number of definitions must be equal") ;
    }

    let initialY = columnDef.startY === undefined ? getCurY() : columnDef.startY ;

    let colDefinitions ;

    if ( columnDef.absoluteColumnCoOrdinates === undefined )
    {
        colDefinitions = getAbsoluteColumnCoOrdinates( { numberOfColumns, leftMargin, rightMargin, columnPadding }) ;
    }
    else
    {
        colDefinitions = columnDef.absoluteColumnCoOrdinates ;
    }

    const pageHeight = getDocHeight();

    const gutterThreshold = pageHeight - bottomMargin;

    let maxY = initialY ;

    // Iterate through each column
    for ( let curCol = 0 ; curCol < numberOfColumns ; curCol ++  )
    {
        // Reset cursor to top of column
        setCurY( initialY ) ;

        const colLeft = colDefinitions[curCol].left ;
        const colWidth = colDefinitions[curCol].width ;

        const paras = content[curCol] ;

        let curPosition = initialY ;

        for ( let curParaOffset = 0 ; curParaOffset < paras.length ; curParaOffset++ )
        {
            const curPara = paras[curParaOffset] ;

            if ( curPara.image !== undefined )
            {
                if ( columnDef.debug === true )
                {
                    const x1 = colLeft;
                    const y1 = curPosition;
                    const x2 = x1 + 10;
                    const y2 = curPosition;

                    doc.setDrawColor('#FF0000');
                    doc.setLineWidth(1);
                    doc.line(x1, y1, x2, y2, 'S');

                    const x11 = colLeft;
                    const y11 = curPosition;
                    const x21 = colLeft;
                    const y21 = curPosition + 10;

                    doc.setDrawColor('#FF0000');
                    doc.setLineWidth(1);
                    doc.line(x11, y11, x21, y21, 'S');
                }

                const rotation = curPara.rotation === undefined ? 0 : curPara.rotation;

                doc.addImage ( curPara.image, 'PNG', colLeft, curPosition, colWidth, colWidth, undefined, undefined, rotation ) ;

                const maxYForThisColumn =  curPosition + lineSpacing + colWidth ;

                maxY = Math.max(maxY, maxYForThisColumn) ;

            }
            else
            {
                const style = curPara.style ;
                const text = curPara.text ;

                const fontName = style?.fontName === undefined ? curFont.fontName : style.fontName ;
                const fontStyle = style?.fontStyle === undefined ? 'normal' : style.fontStyle;
                const fontSize = style?.fontSize === undefined ? +normalFontSize : +style.fontSize;
                const textColor = style?.color === undefined ? '#000000' : style.color;
                const lineHeight = style?.lineHeight === undefined ? getLineSpacingForFontSize(fontSize) : +style.lineHeight;

                doc.setFont ( fontName, fontStyle) ;
                doc.setFontSize(fontSize);
                doc.setTextColor(textColor);

                // Split the text into a number of sentences which will fit the page width
                const splitParagraph = doc.splitTextToSize(text, colWidth);

                // Now output all the paragraph lines, generating a new page if we reach the bottom
                for (let i = 0; i < splitParagraph.length; i++)
                {
                    let sentence = splitParagraph[i];

                    if (curPosition > gutterThreshold)
                    {
                        addPage();

                        // If we have an action to run after a page break, call it now.
                        if ( columnDef.postAddPage !== undefined && typeof columnDef.postAddPage === 'function' )
                        {
                            columnDef.postAddPage() ;
                        }

                        curPosition = getCurY();
                    }

                    // Render the sentence object
                    doc.text(sentence, colLeft, curPosition, { baseline: baseline } );

                    curPosition = curPosition + lineHeight;
                }

                const maxYForThisColumn =  curPosition + lineHeight;

                maxY = Math.max(maxY, maxYForThisColumn) ;
            }
        }

        setCurY( maxY ) ;
    }
}

// This function takes an array of paragraphs, and generates a title and subsequent pages if required, with all
// the paragraphs. Handles pagination and page numbering as required and will split paras scross pages
const outputParagraph = (paragraph, style, columnDef ) =>
{
    const curFont = doc.getFont() ;

    const fontName = style?.fontName === undefined ? curFont.fontName : style.fontName ;
    const fontStyle = style?.fontStyle === undefined ? 'normal' : style.fontStyle;
    const fontSize = style?.fontSize === undefined ? +normalFontSize : +style.fontSize;
    const textColor = style?.color === undefined ? '#000000' : style.color;
    const lineHeight = style?.lineHeight === undefined ? getLineSpacingForFontSize(fontSize) : +style.lineHeight;
    const paragraphSpacing = style?.paragraphSpacing === undefined ? lineHeight : +style.paragraphSpacing;
    const suppressPageBreak = style?.suppressPageBreak === undefined ? false : style.suppressPageBreak ;

    // The following applies to bullets
    const bullet = style?.bullet === undefined ? false : style.bullet ;
    const bulletShape = style?.bulletShape === undefined ? 'circle' : style.bulletShape ;
    const bulletColor = style?.bulletColor === undefined ? '#000000' : style.bulletColor ;
    const indent = style?.indent === undefined ? 0 : style.indent ;
    const indentPixels = indent * 15;

    doc.setFont ( fontName, fontStyle) ;
    doc.setFontSize(fontSize);
    doc.setTextColor(textColor);

    let pageWidth = getDocWidth();
    let pageHeight = getDocHeight();

    let availablePageHeight = pageHeight - (topMargin + bottomMargin);
    let gutterThreshold = pageHeight - bottomMargin;

    let xPos ;
    let availablePageWidth ;

    if ( indent === 0 )
    {
        xPos = leftMargin ;
        availablePageWidth = pageWidth - (leftMargin + rightMargin) ;
    }
    else
    {
        availablePageWidth = pageWidth - (leftMargin + rightMargin) - indentPixels ;
        xPos = leftMargin + indentPixels ;
    }

    if ( columnDef !== undefined )
    {
        const columns = columnDef.columns ;
        const targetColumn = columnDef.targetColumn ;

        switch ( columns )
        {
            case 1:
                xPos = leftMargin  ;
                break ;
            case 2:
                switch ( targetColumn )
                {
                    case 1:
                        xPos = leftMargin  ;
                        break ;
                    case 2:
                        xPos = leftMargin + ( availablePageWidth / 2 ) + columnMargin  ;
                        break ;
                    default:
                        xPos = leftMargin  ;
                        break ;
                }
                break ;
            case 3:
                switch ( targetColumn )
                {
                    case 1:
                        xPos = leftMargin  ;
                        break ;
                    case 2:
                        xPos = leftMargin + ( availablePageWidth / 3 ) + columnMargin  ;
                        break ;
                    case 3:
                        xPos = leftMargin + (( availablePageWidth / 3 ) + columnMargin ) * 2 ;
                        break ;
                }
                break ;
        }
    }

    // Split the text into a number of sentences which will fit the page width
    const splitParagraph = doc.splitTextToSize(paragraph.trim(), availablePageWidth);

    let curPosition = getCurY();

    let writtenAt = [] ;

    // Now output all of the paragraph lines, generating a new page if we reach the bottom
    for (let i = 0; i < splitParagraph.length; i++)
    {
        let sentence = splitParagraph[i];

        if (curPosition > gutterThreshold)
        {
            if ( !suppressPageBreak )
            {
                addPage( true );
            }

            curPosition = getCurY();
        }

        // Render the sentence object
        doc.text(sentence, xPos, curPosition, { baseline: baseline });

        if ( i === 0 )
        {
            if ( bullet )
            {
                if (bulletShape === 'circle')
                {
                    doc.setFillColor (bulletColor);
                    doc.circle(leftMargin + 3, curPosition - 2, 2, 'F');
                }
                else if (bulletShape === 'triangle')
                {
                    const side = 5 ;
                    const halfside = side / 2 ;
                    const triangleY = curPosition -3;

                    const x1 = leftMargin ;
                    const y1 = triangleY - ( side / 2 );

                    const x2 = leftMargin ;
                    const y2 = triangleY + ( side / 2) ;

                    const x3 = leftMargin + Math.sqrt ( ( side * side ) - ( halfside * halfside ) ) ;
                    const y3 = triangleY ;

                    doc.setFillColor (bulletColor);
                    doc.triangle( x1, y1, x2, y2, x3, y3, 'F');
                }
            }
        }

        writtenAt[i] = { x: xPos, y: curPosition, page: currentPage } ;

        curPosition = curPosition + lineHeight;
    }

    curY = curPosition + paragraphSpacing;

    return writtenAt ;
}

const circle = (x, y, r, style) =>
{
    doc.circle(x,y,r,style);
}

const outputFormField = ( x, y, text, style ) =>
{
    const curFont = doc.getFont() ;

    const fontName = style?.fontName === undefined ? curFont.fontName : style.fontName ;
    const fontStyle = style?.fontStyle === undefined ? 'normal' : style.fontStyle;
    const fontSize = style?.fontSize === undefined ? +normalFontSize : +style.fontSize;
    const textColor = style?.color === undefined ? '#000000' : style.color;
    const angle = style.angle === undefined ? 0 : style.angle;

    doc.setFontSize(fontSize);
    doc.setFont ( fontName, fontStyle) ;
    doc.setTextColor(textColor);

    let xOffset = x ;

    if ( x === 'center' )
    {
        const textWidth =  doc.getStringUnitWidth(text) ;

        const fontSize = doc.internal.getFontSize() ;

        const stringWidth = textWidth * fontSize ;

        xOffset = ( doc.internal.pageSize.getWidth()  - stringWidth ) / 2 ;
    }

    const displayText = text === undefined ? 'undefined' : text ;

    doc.text( displayText, xOffset, y, { baseline: baseline, angle: angle });

    doc.setFont ( curFont.fontName, curFont.fontStyle) ;
}

const drawRectangle = ( { x, y, w, h, fillColor }) =>
{
    doc.setFillColor (fillColor);
    doc.rect( x,y,w,h,'F');
}

const outputBulletedParagraph = (paragraph, style, indent) =>
{
    const fontStyle = style?.fontStyle === undefined ? 'normal' : style.fontStyle ;
    const fontSize = style?.fontSize === undefined ? +normalFontSize : +style.fontSize ;
    const textColor = style?.color === undefined ? '#000000' : style.color ;
    const bulletColor = style?.bulletColor === undefined ? '#000000' : style.bulletColor ;
    const bulletShape = style?.bulletShape === undefined ? 'circle' : style.bulletShape ;
    const indentPixels = indent * 15;

    setFontStyle( fontStyle) ;

    let pageWidth = getDocWidth();
    let pageHeight = getDocHeight();

    let availablePageWidth = pageWidth - ( leftMargin + rightMargin ) - indentPixels;
    let availablePageHeight = pageHeight - ( TOP_MARGIN + bottomMargin ) ;
    let gutterThreshold = pageHeight - bottomMargin ;

    // Split the text into a number of sentences which will fit the page width
    const splitParagraph = doc.splitTextToSize(paragraph, availablePageWidth) ;

    let curPosition = getCurY() ;

    // Now output all of the paragraph lines, generating a new page if we reach the bottom
    for (let i = 0; i < splitParagraph.length; i++)
    {
        let sentence = splitParagraph[i] ;

        if ( curPosition > gutterThreshold )
        {
            addPage( true );

            curPosition = getCurY() ;
        }

        doc.setFontSize(fontSize);
        doc.setTextColor ( textColor );

        // Render the sentence object
        doc.text(sentence, leftMargin + indentPixels, curPosition , { baseline: baseline });

        if ( i === 0 ) {
            if (bulletShape === 'circle')
            {
                doc.circle(leftMargin + 3, curPosition - 4, 2, 'F');
            }
            else if (bulletShape === 'triangle')
            {
                const side = 5 ;
                const halfside = side / 2 ;
                const triangleY = curPosition -3;

                const x1 = leftMargin ;
                const y1 = triangleY - ( side / 2 );

                const x2 = leftMargin ;
                const y2 = triangleY + ( side / 2) ;

                const x3 = leftMargin + Math.sqrt ( ( side * side ) - ( halfside * halfside ) ) ;
                const y3 = triangleY ;

                doc.setFillColor (bulletColor);
                doc.triangle( x1, y1, x2, y2, x3, y3, 'F');
            }

        }

        curPosition = curPosition + fontSize + getLineSpacingForFontSize ( fontSize ) ;
    }

    curY = curPosition + lineSpacing;
}

const outputLine = (style) =>
{
    const pageWidth = getDocWidth();
    const curPosition = curY;
    const fillColor = style?.fillColor === undefined ? '#000000' : style.fillColor;
    const width = style?.width === undefined ? 1 : style.width;

    const x1 = leftMargin;
    const y1 = curPosition;
    const x2 = pageWidth - leftMargin;
    const y2 = curPosition;

    doc.setDrawColor(fillColor);
    doc.setLineWidth(width);
    doc.line(x1, y1, x2, y2, 'S');

    curY = curY + normalFontSize + getLineSpacingForFontSize ( normalFontSize ) ;
}

const outputHeader = (text, color, fontSize) =>
{
    const textColor = color === undefined ? '#000000': color;
    const textFontSize = fontSize === undefined ? normalFontSize : fontSize;

    doc.setFontSize(textFontSize);
    doc.setTextColor ( textColor );

    // Render the sentence object
    doc.text(text, leftMargin , curY, { baseline: baseline });

    curY = curY + textFontSize + getLineSpacingForFontSize ( textFontSize ) ;
}

const outputStyledText = (text, style) =>
{
    console.warn("pdf::outputStyledText is Deprecated - Call outputParagraph Instead with paragraphSpacing: 0");
    return outputParagraph( text, style ) ;
}

const outputTable = ( headers, data, params ) =>
{
    let pageWidth = getDocWidth();
    let pageHeight = getDocHeight();

    const columnStyles = params?.columnStyles === undefined ? {text: {cellWidth: 'auto'}, status: {cellWidth: 100}} : params.columnStyles ;
    const theme = params?.theme === undefined ? 'striped' : params.theme;
    const fontSize = params?.fontSize === undefined ? normalFontSize : params.fontSize;
    const cellPadding = params?.cellPadding === undefined ?  cellPaddingDefault : params.cellPadding;
    const showHead = params?.showHead === undefined ? true : params.showHead;
    const fontStyle = params?.fontStyle === undefined ? 'normal' : params.fontStyle ;
    const fontName = params?.fontName === undefined ? 'normal' : params.fontName ;

    let startY = curY;

    doc.autoTable({
        didDrawPage: pageData =>
        {
        },
        didDrawCell: params.didDrawCell,
        startY: startY,
        columnStyles: columnStyles,
        head: headers,
        margin: {bottom: bottomMargin, left: leftMargin, right: rightMargin},
        body: data,
        theme: theme,
        showHead: showHead,
        styles: {fontSize: fontSize, cellPadding: cellPadding, fontStyle: fontStyle, font: fontName},
    });

    curY = doc.lastAutoTable.finalY + titleFontSize + lineSpacing + 6;
}

const outputHalfTable = ( headers, data, params) =>
{
    let pageWidth = getDocWidth()  ;

    const tableWidth = (pageWidth - (leftMargin + rightMargin)) / 2;

    const columnStyles = params?.columnStyles === undefined ? {text: {cellWidth: 'auto'}, status: {cellWidth: 100}} : params.columnStyles ;
    const theme = params?.theme === undefined ? 'striped' : params.theme;

    let startY = curY;

    doc.autoTable({
        didDrawPage: pageData =>
        {
        },
        startY: startY,
        columnStyles: columnStyles,
        head: headers,
        margin: {bottom: bottomMargin, left: leftMargin + tableWidth, right: rightMargin},
        body: data,
        theme: theme
    });

    curY = doc.lastAutoTable.finalY + titleFontSize + lineSpacing + 6;
}

const addPageNumbers = ( numberFirstPage ) =>
{
    const pageCount = doc.internal.getNumberOfPages(); //Total Page Number

    const startPage = numberFirstPage === true ? 1 : 2 ;

    for(let i = startPage ; i < pageCount + 1 ; i++)
    {
        doc.setPage(i);

        let pageCurrent = doc.internal.getCurrentPageInfo().pageNumber; //Current Page

        addPageFooter ( pageCurrent - startPage + 1 );

        // doc.setFontSize(12);
        // doc.text('page: ' + pageCurrent + '/' + pageCount, 10, 10);
    }
}

const decodeEntities = (function() {
    // this prevents any overhead from creating the object each time
    var element = document.createElement('div');

    function decodeHTMLEntities (str) {
        if(str && typeof str === 'string') {
            // strip script/html tags
            str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
            str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '');
            element.innerHTML = str;
            str = element.textContent;
            element.textContent = '';
        }

        return str;
    }

    return decodeHTMLEntities;
})();

const htmlToArray = (html) =>
{
    const content = [] ;

    const v = "<body>" + html + "</body>" ;

    let dom = HTMLtoDOM(v);

    let cc = dom.getElementsByTagName("p") ;

    // Loop though all the <p>  tags and concatenate the strings together
    for ( let o=0; o < cc.length ; o++ )
    {
        let str = decodeEntities(cc[o].firstChild.nodeValue) ;

        if ( str !== null )
        {
            const newStr = str.replace(/<br\/>/gi, '\n')

            const paraObj = newStr ;

            content.push(paraObj);
        }
    }

    return content ;

}

const stream = () =>
{
    // Steve changed this from datauslnewwindow to bloburl to fix a live bug where the PDF couldn't be downloaded in Chrome/Edge
    // This *appears* to have caused a problem where the latest version of the PDF isn't shown to the user, but an
    // older cached one - which isn't fixed when the cache is cleared. It only seems to happen with one school on test, but
    // This is just a placeholder in case this persists in live
    //

    /* const string = doc.output('datauristring');
    const x = window.open();
    x.document.open();
    x.document.location=string; */

    //window.open(doc.output('bloburl', '_blank'));

    // doc.output('dataurlnewwindow', {filename:"neil.pdf"});     //opens the data uri in new window
    window.open(doc.output('bloburl'), '_blank');
}


module.exports = { drawRectangle, getDocHeight, setPage, getCurrentPosition, addImage, tabbedColumnControl, htmlToArray, outputColumns, outputStyledText, lineFeed, getTopMargin, getLeftMargin, getRightMargin, getPrintWidth, initialise, save,circle,  getCurY, setCurY, setFont, outputTextCentred, insertImageCentred, insertDraftWatermark, insertBackgroundImage, insertInvoiceImage, insertAllAboutMeImage, insertGraphImageCentred, drawTriangles, drawBorder, addPageFooter, addPage, addPageNumbers, outputHeader, outputParagraph, outputFormField, outputBulletedParagraph, outputLine, outputTable, outputHalfTable, getDocWidth, stream } ;
